
import {Button, Card, Flex, Space} from "antd";
import Title from "antd/es/typography/Title";
import React, {useCallback, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowDown, faArrowUp, faArrowUpWideShort, faRotate, faTrash} from "@fortawesome/free-solid-svg-icons";
import {DraftAct, DraftChapter} from "../../../../../../feature/Draft";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {aiChainPrompt, similaritySearch} from "../../../../../../ai/Prompt";
import {updateAct, updateChapter} from "../../../../../../feature/project/draftSlice";
import {generateChapterPrompt, generateChaptersPrompt, improveChapterPrompt} from "./prompts/PlotChaptersPrompts";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";

export const PlotActDetails = (props: { act: DraftAct }) => {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(state => state.draftSlice.draft)

    const generateChapters = async (model: string, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: props.act.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: generateChaptersPrompt({
                act: props.act.id,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                acts: draft.story.acts,
                context: context?.documents,
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateAct({act: {...props.act, chapters: data.data}}))
    }

    const improveChapter = async (model: string, chapter: DraftChapter, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: props.act.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: improveChapterPrompt({
                chapter: chapter,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                chapters: props.act.chapters,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateChapter({
            chapter: {
                ...chapter,
                title: data.data.title,
                description: data.data.description ?? "",
                sections: []
            }
        }))
    }

    const generateChapter = async (model: string, chapter: DraftChapter, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: props.act.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: generateChapterPrompt({
                chapter: chapter.id,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                chapters: props.act.chapters,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateChapter({
            chapter: {
                ...chapter,
                title: data.data.title,
                description: data.data.description ?? "",
                sections: []
            }
        }))
    }


    return (
        <Flex style={{padding: 4, flexGrow: 1, height: "100%", overflowY: 'auto', scrollbarWidth: "none"}}
              vertical={true} gap={10}>

            <Card title={props.act.title}>

                <TextAreaActions
                    style={{border: "none"}}
                    value={props.act.description}
                    autoSize={true}
                    validate={text => true}
                    onChange={(text: string) => {
                        dispatch(updateAct({
                            // @ts-ignore
                            act: {
                                ...props.act,
                                description: text
                            }
                        }))
                    }}
                />
            </Card>

            <Card>
                <Flex justify={'space-between'} align={'center'} wrap={'wrap'}>
                    <Title level={5} style={{margin: 0}}>Chapters</Title>
                    <Space>
                        <GenButton
                            style={{width: 130}}
                            type={'primary'}
                            actions={[{
                                key: "1", title: "Generate", action: async (model: string, projectId: string) =>
                                    generateChapters(model, projectId)
                                        .catch((e) => {
                                            console.log(e)
                                        })
                            }]
                            }/>
                    </Space>
                </Flex>
            </Card>
            <Space direction={'vertical'}>
                {props.act.chapters.map((item, index) =>
                    <Card style={{flexGrow: 1}} key={index}
                          title={<Flex justify={'space-between'}>
                              <Flex gap={10} style={{flexGrow: 1}}>
                                  <div style={{
                                      height: 30,
                                      minWidth: 30,
                                      width: 30,
                                      borderRadius: 30,
                                      background: "#ffffff",
                                      color: "#000",
                                      textAlign: "center",
                                      padding: 3
                                  }}>{index + 1}</div>
                                  <DebounceInput
                                      style={{fontWeight: 'bold'}}
                                      placeholder={"Scene title"}
                                      value={item.title}
                                      onChange={(text) => {
                                          dispatch(updateChapter({
                                              // @ts-ignore
                                              chapter: {
                                                  ...item,
                                                  title: text
                                              }
                                          }))
                                      }}/>

                                  <Space>
                                      <Button disabled={index == 0}
                                              type={'text'}
                                              style={{color: index == 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,1)'}}
                                              icon={<FontAwesomeIcon icon={faArrowUp}/>}
                                              onClick={() => {
                                                  let chapters = [...props.act.chapters]
                                                  let prev = {...chapters[index - 1]}
                                                  let current = {...chapters[index]}
                                                  let prevId = prev.id
                                                  prev.id = current.id
                                                  current.id = prevId

                                                  dispatch(updateAct({
                                                      // @ts-ignore
                                                      act: {
                                                          ...props.act,
                                                          chapters: [...chapters.filter((item) => item.id !== prev.id && item.id !== current.id)].concat([prev, current]).sort((a, b) => a.id - b.id) as []
                                                      }
                                                  }))
                                              }}/>
                                      <Button disabled={index == props.act.chapters.length - 1} type={'text'}
                                              style={{color: index == props.act.chapters.length - 1 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,1)'}}
                                              icon={<FontAwesomeIcon icon={faArrowDown}/>} onClick={() => {
                                          let chapters = [...props.act.chapters]
                                          let next = {...chapters[index + 1]}
                                          let current = {...chapters[index]}
                                          let nextId = next.id
                                          next.id = current.id
                                          current.id = nextId

                                          dispatch(updateAct({
                                              // @ts-ignore
                                              act: {
                                                  ...props.act,
                                                  chapters: [...chapters.filter((item) => item.id !== next.id && item.id !== current.id)].concat([current, next]).sort((a, b) => a.id - b.id) as []
                                              }
                                          }))
                                      }}/>
                                      <Button icon={<FontAwesomeIcon icon={faTrash}/>} onClick={() => {

                                          dispatch(updateAct({
                                              // @ts-ignore
                                              act: {
                                                  ...props.act,
                                                  chapters: props.act.chapters.filter((it) => it.id !== item.id) as []
                                              }
                                          }))
                                      }}/>
                                      <GenButton
                                          mode={'icon'}
                                          hideMainAction={true}
                                          icon={<IconBoltGrad/>}
                                          actions={[{
                                              key: "1",
                                              title: "Re-generate",
                                              action: async (model: string, projectId: string) => generateChapter(model, item, projectId),
                                              render: (key, title, action) => {
                                                  return <Space><BoltIcon secondary={faRotate} size={16}/>{title}
                                                  </Space>
                                              }
                                          }, {
                                              key: "2",
                                              title: "Improve",
                                              action: async (model: string, projectId: string) => improveChapter(model, item, projectId),
                                              render: (key, title, action) => {
                                                  return <Space><BoltIcon secondary={faArrowUpWideShort}
                                                                          size={16}/>{title}</Space>
                                              }
                                          }]}/>
                                  </Space>
                              </Flex>
                          </Flex>}>

                        <TextAreaActions
                            style={{border: "none"}}
                            autoSize={true}
                            value={item.description}
                            validate={text => true}
                            onChange={(text: string) => {
                                dispatch(updateChapter({
                                    // @ts-ignore
                                    chapter: {
                                        ...item,
                                        description: text
                                    }
                                }))
                            }}
                        />
                    </Card>
                )}
            </Space>
        </Flex>
    )
}